import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Settings } from '@mui/icons-material';
import { Box, Button, Stack } from '@mui/material';
import { URLPaths } from 'models/enums';
import SuccessDialog from 'domains/core/components/SuccessDialog';
import PageLayout from 'domains/core/components/PageLayout';
import useCreateRole from 'hooks/mutations/useCreateRole';
import useGetPermissionsList from 'hooks/queries/useGetPermissionsList';
import RoleDetailsCard from './RoleDetailsCard';
import AssignPermissionsCard from './AssignPermissionsCard';

const PAGE_HEADER = 'Create New Role';
const CREATE_ROLE_BUTTON_LABEL = 'Create Role';
const SUCCESS_DIALOG_TITLE = 'Role created';
const SUCCESS_DIALOG_BODY = 'The role has been created successfully.';
const BACK_TO_ROLES_BUTTON_LABEL = 'Back to Roles list';

const MIN_ROLE_NAME_LENGTH = 3;

const CreateRolePage = () => {
    const history = useHistory();
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [permissionIds, setPermissionIds] = useState<number[]>([]);
    const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);

    const createRoleMutation = useCreateRole({
        onSuccess: () => setIsSuccessDialogOpen(true),
    });
    const { data: permissionsData, isLoading: isPermissionsLoading } = useGetPermissionsList({
        limit: 100,
        offset: 0,
    });

    const permissions = permissionsData?.data ?? [];
    const totalPermissionCount = permissionIds.length;
    const modulesConfiguredCount = new Set(permissions.filter((p) => permissionIds.includes(p.id)).map((p) => p.module))
        .size;

    const canCreate =
        name.trim().length >= MIN_ROLE_NAME_LENGTH && permissionIds.length >= 1 && !createRoleMutation.isLoading;

    const handleCreateRole = () => {
        if (!canCreate) return;
        createRoleMutation.mutate({
            name: name.trim(),
            description: description.trim() || undefined,
            permissionIds,
        });
    };

    const handleBackToRolesList = () => {
        history.push(URLPaths.SETTINGS_ROLES);
    };

    const headerAction = (
        <Button
            variant="contained"
            onClick={handleCreateRole}
            disabled={!canCreate}
            data-testid="access-center-create-role-submit-button"
        >
            {CREATE_ROLE_BUTTON_LABEL}
        </Button>
    );

    return (
        <Stack data-testid="access-center-create-role-page" role="main" aria-label={PAGE_HEADER}>
            <PageLayout header={PAGE_HEADER} headerIcon={<Settings fontSize="inherit" />} headerAction={headerAction}>
                <Stack direction="row" spacing={3} sx={{ width: '100%' }}>
                    <Box sx={{ flex: '0 0 40%', minWidth: 0 }}>
                        <RoleDetailsCard
                            name={name}
                            description={description}
                            onNameChange={setName}
                            onDescriptionChange={setDescription}
                            totalPermissionCount={totalPermissionCount}
                            modulesConfiguredCount={modulesConfiguredCount}
                        />
                    </Box>
                    <Box sx={{ flex: '0 0 60%', minWidth: 0 }}>
                        <AssignPermissionsCard
                            permissions={permissions}
                            selectedPermissionIds={permissionIds}
                            onSelectionChange={setPermissionIds}
                            isLoading={isPermissionsLoading}
                        />
                    </Box>
                </Stack>
            </PageLayout>
            <SuccessDialog
                title={SUCCESS_DIALOG_TITLE}
                body={SUCCESS_DIALOG_BODY}
                isOpen={isSuccessDialogOpen}
                setIsOpen={setIsSuccessDialogOpen}
                onClose={() => setIsSuccessDialogOpen(false)}
                buttonOneText="Close"
                buttonTwoText={BACK_TO_ROLES_BUTTON_LABEL}
                buttonOneOnClick={() => {}}
                buttonTwoOnClick={handleBackToRolesList}
            />
        </Stack>
    );
};

export default CreateRolePage;
